import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import UnityPlayer, { TrackedComponent } from './UnityPlayer'
import AccessibleMultipleChoiceGame from './AccessibleMultipleChoiceGame'
import DaxAnimation, { DaxReaction, DAX_COMPOSITION_FILL, DAX_HEAD_TOP_FILL, DAX_ART_BOTTOM_FILL } from './DaxAnimation/DaxAnimation'
import { PostAnswerData, McqGameInputData } from '../../models/global.types'
// PreferenceType import removed as unused
import { logger } from '../../utils'
import { parseMcqWrongAnswerSelection, resolveDaxExplanationAudioUrlsForQuestions, resolveDaxExplanationSubtitleUrlsForQuestions } from '../../utils/audio'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { useMcqSoundFx } from '../../hooks/useMcqSoundFx'
import { hasDaxAnimationFeatureFlag } from '../molecules/URLFeatureFlag/URLFeatureFlag'
import {
  getUnityAnswerLayout,
  getUnityUiScale,
  getUnityWidthSquash,
  SPEECH_BUBBLE_WIDTH,
  SPEECH_BUBBLE_HEIGHT
} from '../../utils/unityLayout'

export { SPEECH_BUBBLE_WIDTH, SPEECH_BUBBLE_HEIGHT }

// Speech-bubble art (golden bubble + downward tail + baked shadow), served from /public. The filename
// has spaces, so encodeURI keeps the src valid. Native art size is 304x146.
const SPEECH_BUBBLE_URL = encodeURI(`${process.env.PUBLIC_URL ?? ''}/assets/images/Speech bubble - Wrong answer.svg`)

/**
 * Golden speech bubble, rendered from the Speech bubble - Wrong answer.svg art. Timed VTT cues from
 * the playing explanation MP3 are overlaid in the body (padding keeps them out of the tail). It is
 * centered horizontally (left:50%) and its TOP edge sits at `top` (any CSS length), so it hangs
 * downward from that line. Leaving `height` off keeps the art's native aspect ratio; passing it pins
 * both dimensions. `interactive` is false in the Unity view so the bubble never blocks in-engine
 * clicks. The whole bubble is hidden from ARIA — it is decorative; Dax's line is the spoken MP3.
 *
 * `fixed` switches the positioning context from the nearest positioned ancestor (the accessible view's
 * Dax box) to the viewport itself (the standard/Unity view, where the bubble is placed independently of
 * Dax rather than stacked on his box - see the standard-view render for why). It also bumps the z-index
 * above Dax's own box (z-50): nested, the bubble's z-20 only had to beat the canvas within their shared
 * stacking context, but as a sibling it needs to outrank the whole boxed context to still read as in
 * front of him now that overlapping him is expected rather than prevented.
 */
const DaxSpeechBubble: React.FC<{ interactive?: boolean, top?: string, width?: number, height?: number, fixed?: boolean, className?: string, caption?: string }> = ({
  interactive = true,
  top = '0px',
  width = SPEECH_BUBBLE_WIDTH,
  height,
  fixed = false,
  className,
  caption
}) => (
  <div
    data-testid='dax-speech-bubble'
    // Purely decorative: the bubble adds no meaningful content for a non-sighted user, so it is
    // hidden from the accessibility tree (and its descendants) rather than given a description.
    aria-hidden='true'
    role='presentation'
    className={clsx(fixed ? 'fixed z-[60]' : 'absolute z-20', '-translate-x-1/2', interactive ? 'pointer-events-auto' : 'pointer-events-none', className)}
    style={{ left: '50%', top }}
  >
    <img
      src={SPEECH_BUBBLE_URL}
      alt=''
      aria-hidden='true'
      draggable={false}
      className='block max-w-none select-none'
      style={{ width: `${width}px`, height: height !== undefined ? `${height}px` : undefined }}
    />
    {caption != null && caption !== '' && (
      <div
        data-testid='dax-speech-bubble-caption'
        className='absolute inset-0 flex items-center justify-center overflow-hidden px-[10%] pb-[22%] pt-[8%] text-center text-sm font-semibold leading-snug text-neutral-900 whitespace-pre-line'
      >
        {caption}
      </div>
    )}
  </div>
)

// Dax and his saucer take a fixed share of the 3/5 height between the health bar and the buttons. The
// two bubble shares below are what is left for the bubble and the gap under it; the accessible bubble is
// now drawn at a fixed size from design instead of that share, so it may overhang the top of the budget.
const ACCESSIBLE_BUBBLE_HEIGHT_SHARE = 0.24
const ACCESSIBLE_BUBBLE_GAP_SHARE = 0.04
const ACCESSIBLE_CANVAS_SHARE = 1 - ACCESSIBLE_BUBBLE_HEIGHT_SHARE - ACCESSIBLE_BUBBLE_GAP_SHARE
// Design-specified bubble size for the accessible view (px). Slightly squashed relative to the art's
// native 304x146, so both dimensions are pinned rather than derived from the aspect ratio.
const ACCESSIBLE_BUBBLE_WIDTH = 298
const ACCESSIBLE_BUBBLE_HEIGHT = 138
// Used only until the gap has been measured.
const ACCESSIBLE_BUBBLE_FALLBACK_TOP = '0px'
// Fraction of the health-bar-to-buttons gap the whole Dax composition is allowed to occupy (per the ticket).
const ACCESSIBLE_COMPOSITION_SHARE = 3 / 5
/**
 * Height (px) of Dax's canvas for a given health-bar-to-buttons gap. The canvas is taller than the art it
 * draws, so the canvas share of the budget is divided by DAX_COMPOSITION_FILL to make the VISIBLE art
 * (head crown down to the saucer's glow) hit that share. Both views go through this so Dax is the same
 * size in each one even when the tuning constants in DaxAnimation change.
 */
const canvasHeightForGap = (gap: number): number =>
  (gap * ACCESSIBLE_COMPOSITION_SHARE * ACCESSIBLE_CANVAS_SHARE) / DAX_COMPOSITION_FILL
// The Unity view's health bar and buttons are drawn in-engine, so there is nothing to measure there.
// Its gap is the same as the accessible layout's: a 90vh container, less the 112px the GO button sits
// above the container's bottom edge, plus the 24px the health bar sits above its top edge.
const LAYOUT_HEIGHT_VH = 0.9
const LAYOUT_GAP_INSET_PX = 112 - 24
const unityGap = (height: number): number => height * LAYOUT_HEIGHT_VH - LAYOUT_GAP_INSET_PX
// Where Dax stands: his base is pinned this far above the bottom of the viewport, just above the
// in-engine CONTINUE button, and stays there whatever size he is drawn at.
export const UNITY_DAX_BASE_VH = 0.13
// Clearance between the bottom of the speech bubble and the crown of Dax's head (px), used as the
// signal for how far below the ceiling his head has dropped. Fixed, because the bubble is a fixed
// size - a proportional gap is what let the two drift apart in the original (pre-DMVP-1078) layout.
const UNITY_BUBBLE_HEAD_GAP = 8
// How aggressively the bubble covers the ceiling→floor travel as his head drops. 1 tracks his head
// 1:1; higher values reach the floor sooner as the window narrows (3.5 hits it around ~1500px wide
// at 1080 tall), so it spends less time overlapping the answer tiles on the way down.
const UNITY_BUBBLE_DROP_SPEED = 3.5
// How far below the gap centre the locked floor sits, in reference pixels (scaled with the UI). A
// small nudge so the resting spot reads a little lower than dead-centre of the four tiles, without
// dropping all the way to the bottom row.
const UNITY_BUBBLE_FLOOR_NUDGE = 46
// Exponent on the width-squash factor that sizes Dax. 1 tracks the game's UI scale 1:1; higher values
// shrink him faster as the window narrows (so he ducks out from under the bubble sooner) without
// changing his size at ordinary windows where the squash is still 1.
const UNITY_DAX_WIDTH_SQUASH_POWER = 1.5
// Dax stops shrinking here, however tight the window gets, so he never dwindles to nothing. Low
// enough that the floor only bites at window widths well below the ones people actually work at.
export const UNITY_DAX_MIN_HEIGHT = 120

interface UnityDaxGeometry {
  /** Height of Dax's canvas (px). */
  height: number
  /** Top offset of the speech bubble (px, from the top of the VIEWPORT - see unityDaxGeometry). */
  bubbleTop: number
}

/**
 * Sizes Dax and places the speech bubble in the standard view.
 *
 * Dax keeps his own simple proportional sizing: the same gap budget the accessible view uses, squashed
 * by how narrow the window is so he shrinks with the width too - then raised to
 * UNITY_DAX_WIDTH_SQUASH_POWER so he shrinks faster than the tiles as the window narrows - floored at
 * UNITY_DAX_MIN_HEIGHT so he never dwindles to nothing. After the bubble's resting spot is known, his
 * height is also capped so the crown of his head stays UNITY_BUBBLE_HEAD_GAP below the bubble's bottom
 * edge: he ducks under it rather than growing up into it.
 *
 * The bubble's position is clamped between two fixed lines, with his (independently-sized) head as the
 * thing that moves it between them:
 *
 * - a ceiling: the top of the answer area (`answersTopY`). This is where it sits at max/ordinary window
 *   shapes - high in the empty middle column, level with the top of the upper tiles, rather than sunk
 *   into the cross between all four.
 * - a floor: a little below the gap between all four tiles (`gapCentreY` minus
 *   UNITY_BUBBLE_FLOOR_NUDGE) - near the middle of the cross, and as far down as it is ever allowed
 *   to go. Once it lands here, further shrinking holds it put rather than letting it sink lower;
 *   growing the window is what lifts it back toward the ceiling. Below a certain window height that
 *   line sits ABOVE the ceiling if left alone; it is held to the ceiling itself in that case (i.e. it
 *   never goes below the floor, but the floor never rises above the ceiling either) rather than being
 *   allowed to invert.
 *
 * Between those two, how far his head has dropped below the ceiling is multiplied by
 * UNITY_BUBBLE_DROP_SPEED so the bubble covers the travel faster than he shrinks - it reaches the
 * floor sooner as the window narrows, rather than trailing him 1:1 the whole way.
 */
export const unityDaxGeometry = (
  width: number = window.innerWidth,
  height: number = window.innerHeight
): UnityDaxGeometry => {
  const squash = getUnityWidthSquash(width, height)
  const proportional = canvasHeightForGap(unityGap(height)) * Math.pow(squash, UNITY_DAX_WIDTH_SQUASH_POWER)
  let daxHeight = Math.max(UNITY_DAX_MIN_HEIGHT, proportional)
  const baseY = height * UNITY_DAX_BASE_VH
  const scale = getUnityUiScale(width, height)
  const { answersTopY, gapCentreY } = getUnityAnswerLayout(width, height)

  // All expressed as a CSS "top" offset (px from the top of the viewport) - the smaller the value, the
  // higher up the screen it is. The floor sits a little below the gap centre (nudge grows the CSS top).
  const headFollowTop = height - baseY - daxHeight * (1 - DAX_HEAD_TOP_FILL) - UNITY_BUBBLE_HEAD_GAP - SPEECH_BUBBLE_HEIGHT
  const ceilingTop = height - answersTopY
  const floorTop = Math.max(
    ceilingTop,
    height - gapCentreY - SPEECH_BUBBLE_HEIGHT / 2 + UNITY_BUBBLE_FLOOR_NUDGE * scale
  )
  const headDrop = Math.max(0, headFollowTop - ceilingTop)
  const acceleratedTop = ceilingTop + headDrop * UNITY_BUBBLE_DROP_SPEED
  const bubbleTop = Math.min(floorTop, acceleratedTop)

  // Shrink him further if his head would poke into the bubble - base stays put, so this only pulls
  // his crown down. Never below UNITY_DAX_MIN_HEIGHT.
  const maxHeightUnderBubble =
    (height - baseY - bubbleTop - SPEECH_BUBBLE_HEIGHT - UNITY_BUBBLE_HEAD_GAP) / (1 - DAX_HEAD_TOP_FILL)
  daxHeight = Math.max(UNITY_DAX_MIN_HEIGHT, Math.min(daxHeight, maxHeightUnderBubble))

  return {
    height: daxHeight,
    bubbleTop
  }
}

// The Dax composition (character + saucer + speech bubble) is presented to assistive tech as a
// single, non-interactive image: the wrapper carries role="img" + this label, and every inner
// part is aria-hidden so nothing is announced twice.
const DAX_ARIA_LABEL = 'Dax, an animated guide character'

interface MultipleChoiceQuestionsProps {
  accessibilityView: boolean
  multipleChoiceQuestionData: McqGameInputData | null
  onEndGame: (questionAndAnswerData: PostAnswerData[]) => Promise<void> | void
  onNewLightningModule: (level: string) => void
  onUpdateXP: (questionAndAnswerData: PostAnswerData[]) => void
  trackedComponents?: TrackedComponent[]
}

const MultipleChoiceQuestions: React.FC<MultipleChoiceQuestionsProps> = ({
  accessibilityView,
  multipleChoiceQuestionData,
  onEndGame,
  onNewLightningModule,
  onUpdateXP, // only fires when questions are answered correctly.
  trackedComponents = []
}: MultipleChoiceQuestionsProps) => {
  const [payloadToUnity, setPayloadToUnity] = useState<any>(null)
  const [gameEnded, setGameEnded] = useState<boolean>(false)
  const [reduceMotion, setReduceMotion] = useState<boolean>(false)
  const [daxReaction, setDaxReaction] = useState<DaxReaction>('idle')
  const [daxReactionKey, setDaxReactionKey] = useState<number>(0)
  // Standard (Unity) view only: Dax is a reaction to a mistake. The canvas is pre-mounted (opacity-0)
  // from quiz start so a wrong answer can reveal him immediately instead of waiting on WebGL boot.
  // He hides again on Unity Continue (`McqContinue`) without unmounting.
  const [daxVisibleInUnity, setDaxVisibleInUnity] = useState<boolean>(false)
  // Accessible view: same idea (DMVP-1090 kept him up for the whole quiz; this is the follow-up ticket
  // that gates him on a wrong answer there too). Shown the instant AccessibleMultipleChoiceGame's
  // onIncorrectAnswer fires, hidden again once its onContinue fires.
  const [daxVisibleAccessible, setDaxVisibleAccessible] = useState<boolean>(false)
  // The speech bubble is plain HTML and paints immediately, while Dax is a WebGL canvas that needs a
  // moment to come up, so the composition is held at opacity-0 until he reports his first drawn frame
  // and the whole thing then appears at once. (opacity-0 rather than visibility:hidden so the canvas
  // keeps a real layout box for its first measure — see DMVP-1094.)
  const [daxReady, setDaxReady] = useState<boolean>(false)
  // Accessible-view Dax canvas height (px): sized so the drawn art fills the canvas share of the 3/5.
  const [daxAccessibleHeight, setDaxAccessibleHeight] = useState<number | null>(null)
  // Accessible-view Dax box top (px, relative to the container): positioned so the bottom of the art
  // (the saucer's glow) lands on the top edge of the RESET/GO buttons.
  const [daxAccessibleTop, setDaxAccessibleTop] = useState<number | null>(null)
  // Accessible-view bubble top (px), relative to the top of Dax's canvas. It goes negative because the
  // bubble stacks ABOVE the canvas.
  const [bubbleTop, setBubbleTop] = useState<number | null>(null)
  // Unity-view Dax size and bubble offset. Nothing in that view is measurable from the DOM, so both
  // are solved from the layout's known geometry (see unityDaxGeometry).
  const [unityDax, setUnityDax] = useState<UnityDaxGeometry>(() => unityDaxGeometry())
  // Standard-view Canvas: the first WebGL mount after login often sticks undersized; navigating away
  // and back remounts him at the right size. We do that remount once ourselves before revealing
  // (DMVP-1094), keyed off this generation counter.
  const [unityDaxMountGen, setUnityDaxMountGen] = useState(0)
  const unityDaxHasRemounted = useRef(false)
  const daxContainerRef = useRef<HTMLDivElement>(null)
  // Selected option IDs from the last miss. The clip picker compares these to is_correct.
  const [daxWrongQuestionId, setDaxWrongQuestionId] = useState<string | undefined>(undefined)
  const [daxSelectedAnswerIds, setDaxSelectedAnswerIds] = useState<string[]>([])
  const userPreferences = useUserPreferences()
  const responsiveUnity = 'h-[90vh]'
  const soundFx = useMcqSoundFx(multipleChoiceQuestionData?.levelName ?? '')

  // Whether Dax is part of this quiz at all. When he is, both views gate him on a wrong answer
  // (Unity: daxVisibleInUnity; accessible: daxVisibleAccessible).
  const showDax = hasDaxAnimationFeatureFlag()
  const daxExplanationAudioUrls = useMemo(
    () => resolveDaxExplanationAudioUrlsForQuestions(
      multipleChoiceQuestionData?.questions,
      daxWrongQuestionId,
      daxSelectedAnswerIds
    ),
    [multipleChoiceQuestionData?.questions, daxWrongQuestionId, daxSelectedAnswerIds]
  )
  const daxExplanationSubtitleUrls = useMemo(
    () => resolveDaxExplanationSubtitleUrlsForQuestions(
      multipleChoiceQuestionData?.questions,
      daxWrongQuestionId,
      daxSelectedAnswerIds
    ),
    [multipleChoiceQuestionData?.questions, daxWrongQuestionId, daxSelectedAnswerIds]
  )
  const daxSelectionAttrs = daxSelectedAnswerIds.length > 0
    ? {
        'data-dax-selected-answer-ids': daxSelectedAnswerIds.join(','),
        'data-dax-explanation-audio': daxExplanationAudioUrls.join(','),
        'data-dax-explanation-subtitles': daxExplanationSubtitleUrls.join(',')
      }
    : {}

  // Sets Dax's reaction and bumps the key so it re-triggers even on consecutive identical answers.
  const triggerDaxReaction = (reaction: DaxReaction): void => {
    setDaxReaction(reaction)
    setDaxReactionKey((key) => key + 1)
  }

  // Fire Dax's celebratory reaction on a correct answer.
  const triggerDaxCorrect = (): void => triggerDaxReaction('correct')

  // Fire Dax's shake reaction on a wrong answer. Both views bring him on screen for the mistake.
  const triggerDaxIncorrect = (): void => triggerDaxReaction('incorrect')

  // Standard view: first onReady remounts the Canvas (DMVP-1094 size fix) while he is still
  // opacity-0 during pre-mount; the second onReady marks him ready so a later wrong answer can
  // reveal immediately. Accessible view is not pre-mounted and has no remount, so it reveals on
  // the first ready.
  const handleDaxReady = useCallback((): void => {
    if (!accessibilityView && !unityDaxHasRemounted.current) {
      unityDaxHasRemounted.current = true
      setUnityDax(unityDaxGeometry())
      setUnityDaxMountGen((gen) => gen + 1)
      return
    }
    setDaxReady(true)
  }, [accessibilityView])

  const playDaxExplanation = (selectedAnswerIds: string[], questionId?: string): void => {
    soundFx.playExplanation(
      resolveDaxExplanationAudioUrlsForQuestions(
        multipleChoiceQuestionData?.questions,
        questionId,
        selectedAnswerIds
      ),
      resolveDaxExplanationSubtitleUrlsForQuestions(
        multipleChoiceQuestionData?.questions,
        questionId,
        selectedAnswerIds
      )
    )
  }

  // Reveal Dax with his shake reaction (standard view). The canvas is already mounted; this only
  // flips visibility. Re-solve geometry in case the window changed while he was hidden.
  const showDaxForWrongAnswer = (selectedAnswerIds: string[] = [], questionId?: string): void => {
    playDaxExplanation(selectedAnswerIds, questionId)
    setDaxSelectedAnswerIds(selectedAnswerIds)
    setDaxWrongQuestionId(questionId)
    setUnityDax(unityDaxGeometry())
    setDaxVisibleInUnity(true)
    triggerDaxIncorrect()
  }

  // Hide him without tearing down the canvas, so the next wrong answer is instant. Idle the
  // reaction so a later remount (game end / leave) does not start mid-shake.
  const hideDaxOnContinue = (): void => {
    soundFx.stopExplanation()
    setDaxVisibleInUnity(false)
    setDaxReaction('idle')
    setDaxSelectedAnswerIds([])
    setDaxWrongQuestionId(undefined)
  }

  // Accessible view: AccessibleMultipleChoiceGame calls this the instant a submitted answer is wrong.
  const showDaxForWrongAnswerAccessible = (selectedAnswerIds: string[] = [], questionId?: string): void => {
    playDaxExplanation(selectedAnswerIds, questionId)
    setDaxSelectedAnswerIds(selectedAnswerIds)
    setDaxWrongQuestionId(questionId)
    setDaxVisibleAccessible(true)
    triggerDaxIncorrect()
  }

  // Accessible view: fires when CONTINUE is pressed, whether the answer that led to it was right or
  // wrong - Dax should not carry over into the next question either way. Drops the ready flag since the
  // canvas unmounts with him, so the next wrong answer starts the "wait for first frame" gating fresh.
  const hideDaxOnContinueAccessible = (): void => {
    soundFx.stopExplanation()
    setDaxVisibleAccessible(false)
    setDaxReaction('idle')
    setDaxReady(false)
    setDaxSelectedAnswerIds([])
    setDaxWrongQuestionId(undefined)
  }

  // Sync reduce motion preference from context
  useEffect(() => {
    setReduceMotion(userPreferences.preferenceA11yReduceMotion)
  }, [userPreferences.preferenceA11yReduceMotion])

  // Per the ticket, the accessible-view Dax composition occupies 3/5 of the vertical space between the
  // health bar (top) and the RESET/GO buttons (bottom). Dax and his saucer are sized from that budget so
  // they scale with the measured gap; the bubble is a fixed size from design and is stacked on top of
  // them. Both anchors are DOM here, so measure the live gap and keep it in sync on resize.
  useEffect(() => {
    if (!accessibilityView || !showDax) return

    const measure = (): void => {
      const container = daxContainerRef.current
      const healthBar = trackedComponents.find((c) => c.id === 'pointBar')?.ref.current
      const goButton = document.getElementById('button-go')
      if (container == null || healthBar == null || goButton == null) return
      const goRect = goButton.getBoundingClientRect()
      const gap = goRect.top - healthBar.getBoundingClientRect().bottom
      if (gap <= 0) return

      const total = gap * ACCESSIBLE_COMPOSITION_SHARE
      const boxHeight = canvasHeightForGap(gap)
      setDaxAccessibleHeight(boxHeight)

      // Sit the bottom of the art - the last ring of the saucer's glow - on the TOP edge of the RESET/GO
      // buttons. It is DAX_ART_BOTTOM_FILL of the canvas height above the canvas's bottom edge.
      const containerTop = container.getBoundingClientRect().top
      const buttonTopInContainer = goRect.top - containerTop
      const boxTopInContainer = buttonTopInContainer - boxHeight * (1 - DAX_ART_BOTTOM_FILL)
      setDaxAccessibleTop(boxTopInContainer)

      // Stack the bubble directly above Dax's head, which sits DAX_HEAD_TOP_FILL of the canvas height
      // below the canvas top. The result is negative (the bubble overhangs the canvas), which is fine.
      setBubbleTop(boxHeight * DAX_HEAD_TOP_FILL - total * ACCESSIBLE_BUBBLE_GAP_SHARE - ACCESSIBLE_BUBBLE_HEIGHT)
    }

    const raf = requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
    }
  }, [accessibilityView, showDax, trackedComponents, multipleChoiceQuestionData])

  // Keep the Unity view's Dax in step with the viewport: both his size and the bubble's offset are
  // derived from it, and the answer tiles move with the window in both dimensions.
  useEffect(() => {
    if (accessibilityView || !showDax) return

    const measure = (): void => {
      setUnityDax(unityDaxGeometry())
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [accessibilityView, showDax])

  useEffect(() => {
    if (!accessibilityView) {
      // Update object to format needed for unity player
      let newPayload = null
      if (multipleChoiceQuestionData !== null && multipleChoiceQuestionData !== undefined) {
        logger.log(`🎯 MultipleChoiceQuestions: Building Unity payload with reduceMotion: ${String(reduceMotion)}`)

        newPayload = {
          level_name: multipleChoiceQuestionData.levelName,
          questions: JSON.stringify({ questions: multipleChoiceQuestionData.questions }),
          totalScore: multipleChoiceQuestionData.totalScore,
          accessibility: {
            reduceMotion
          }
        }

        logger.log('🎯 MultipleChoiceQuestions: Complete payload:')
        logger.log(JSON.stringify(newPayload))
      }
      setPayloadToUnity(newPayload)
    }
  }, [accessibilityView, multipleChoiceQuestionData, reduceMotion])

  const handleUnityUpdateXP = (json: string): void => {
    const answerData: PostAnswerData[] = JSON.parse(json).answers
    if (showDax) triggerDaxCorrect()
    onUpdateXP(answerData)
  }

  // Unity McqWrongAnswer always plays the wrong-answer SFX. Dax only comes on when his feature flag
  // is on; the SFX hook has its own (broader) gate.
  const handleUnityWrongAnswer = (json?: string): void => {
    soundFx.playWrongAnswer()
    if (!showDax) return
    const { questionId, selectedAnswerIds } = parseMcqWrongAnswerSelection(json)
    showDaxForWrongAnswer(selectedAnswerIds, questionId)
  }

  // Accessible view: wrap onUpdateXP (fires only on a correct answer) so Dax reacts there too.
  const handleAccessibleUpdateXP = (answerData: PostAnswerData[]): void => {
    if (showDax) triggerDaxCorrect()
    onUpdateXP(answerData)
  }

  const handleUnityEndGame = (json: string): void => {
    const answerData: PostAnswerData[] = JSON.parse(json).answers
    void onEndGame(answerData)
    setGameEnded(true)
  }

  if (multipleChoiceQuestionData !== null && multipleChoiceQuestionData !== undefined) {
    if (accessibilityView) {
      const game = (
        <AccessibleMultipleChoiceGame
          onContinue={showDax ? hideDaxOnContinueAccessible : undefined}
          onEndGame={onEndGame}
          onIncorrectAnswer={showDax ? showDaxForWrongAnswerAccessible : undefined}
          onUpdateXP={showDax ? handleAccessibleUpdateXP : onUpdateXP}
          questionsData={multipleChoiceQuestionData}
        />
      )
      if (!showDax) return game
      // Dax stands on the right side, horizontally centered over the 1/6-from-the-right
      // mark of the (full-width) health bar (left-[83.333%] == 5/6 across == 1/6 from the
      // right). Height is derived from the measured gap (falls back to 60vh until measured).
      // Like the standard view (see DMVP-1088's "Appearing all at once"), the composition is held at
      // opacity-0 until Dax reports his first drawn frame: the speech bubble is plain HTML and paints
      // immediately, while Dax is a WebGL canvas that needs a moment to load, so without this the
      // bubble would flash on screen alone before he pops in.
      return (
        <div ref={daxContainerRef} className='relative'>
          {game}
          {daxVisibleAccessible &&
            <div
              role='img'
              aria-label={DAX_ARIA_LABEL}
              {...daxSelectionAttrs}
              // opacity-0 (not visibility:hidden / `invisible`): the WebGL canvas must keep a real
              // layout box so its first measure is correct. visibility:hidden left Dax undersized
              // on the first show after login until a remount (DMVP-1094). aria-hidden covers a11y
              // until he has actually drawn.
              aria-hidden={!daxReady}
              className={clsx('pointer-events-none absolute left-[83.333%] -translate-x-1/2 w-[26%] z-10', !daxReady && 'opacity-0')}
              style={{ height: daxAccessibleHeight ?? '60vh', top: daxAccessibleTop ?? '20%' }}
            >
              <DaxSpeechBubble
                top={bubbleTop !== null ? `${bubbleTop}px` : ACCESSIBLE_BUBBLE_FALLBACK_TOP}
                width={ACCESSIBLE_BUBBLE_WIDTH}
                height={ACCESSIBLE_BUBBLE_HEIGHT}
                caption={soundFx.explanationCaption}
              />
              <DaxAnimation
                className='w-full h-full'
                onReady={handleDaxReady}
                overrideClip='Nod'
                reaction={daxReaction}
                reactionKey={daxReactionKey}
                reduceMotion={reduceMotion}
                showPlatform
              />
            </div>}
        </div>
      )
    } else {
      return (
        <div className={clsx(responsiveUnity, 'flex justify-center relative')}>
          {!gameEnded &&
            <UnityPlayer
              onEndGame={handleUnityEndGame}
              onNewLightningModule={onNewLightningModule}
              onUpdateXP={handleUnityUpdateXP}
              payloadToUnity={payloadToUnity}
              trackedComponents={trackedComponents}
              onMcqSubmit={soundFx.playSubmit}
              onMcqReset={soundFx.playReset}
              onMcqOptionSelected={soundFx.playOptionSelect}
              onMcqWrongAnswer={handleUnityWrongAnswer}
              onMcqRightAnswer={soundFx.playRightAnswer}
              onMcqContinue={showDax ? hideDaxOnContinue : undefined}
            />}
          {/* Unity is position:fixed (z-index 1) and starts at the measured bar top, so the health
              bar stays painted on top of the question. While a question is open the bar ignores
              pointer events so the drag icons can be grabbed where they overlap it. Dax must also
              be fixed with a higher z-index to overlay the canvas. The buttons are in-engine (not
              DOM), so the gap can't be measured here — the height comes from canvasHeightForGap,
              the same function the accessible view uses, so Dax's body is identical in both views. */}
          {!gameEnded && showDax &&
            <>
              <div
                role='img'
                aria-label={DAX_ARIA_LABEL}
                {...daxSelectionAttrs}
                // Pre-mounted at opacity-0 so WebGL is warm before the first wrong answer. Remount
                // (unityDaxMountGen) still runs once on first ready (DMVP-1094). Revealed only when
                // he is both ready and a wrong answer has asked him on screen.
                aria-hidden={!daxVisibleInUnity || !daxReady}
                className={clsx('pointer-events-none fixed left-1/2 -translate-x-1/2 z-50', (!daxVisibleInUnity || !daxReady) && 'opacity-0')}
                style={{ width: 300, height: unityDax.height, bottom: `${UNITY_DAX_BASE_VH * 100}vh` }}
              >
                <DaxAnimation
                  key={`unity-dax-${unityDaxMountGen}`}
                  width={300}
                  height={unityDax.height}
                  onReady={handleDaxReady}
                  overrideClip='Nod'
                  reaction={daxReaction}
                  reactionKey={daxReactionKey}
                  reduceMotion={reduceMotion}
                  showPlatform
                />
              </div>
              {/* Positioned independently of Dax's box (see unityDaxGeometry): high at the top of the
                  answer area when there's room, walking down to the middle of the four tiles as the
                  window shrinks, then holding there - growing the window is what lifts it back up.
                  Not pre-mounted: the bubble is plain HTML and paints instantly with the reveal. */}
              {daxVisibleInUnity &&
                <DaxSpeechBubble
                  fixed
                  interactive={false}
                  top={`${unityDax.bubbleTop}px`}
                  height={SPEECH_BUBBLE_HEIGHT}
                  className={!daxReady ? 'opacity-0' : undefined}
                  caption={soundFx.explanationCaption}
                />}
            </>}
        </div>
      )
    }
  } else {
    return null
  }
}

export default React.memo(MultipleChoiceQuestions)
